import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, MapPin, Clock, Wrench, Edit, Settings } from 'lucide-react';
import { DealerLocation } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
`;

const LocationCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
  }
  
  .location-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    
    .location-name {
      font-weight: 600;
      color: #1f2937;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  
  .location-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
    font-size: 0.875rem;
    color: #6b7280;
    
    .detail-item {
      display: flex;
      flex-direction: column;
      
      .label {
        font-weight: 500;
        color: #374151;
        margin-bottom: 2px;
      }
    }
  }
  
  .location-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid #d1d5db'};
  background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    ${props => props.variant === 'primary' ? `
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    ` : `
      background: #f3f4f6;
      border-color: #9ca3af;
    `}
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  
  h3 {
    margin: 0 0 8px;
    color: #374151;
  }
  
  p {
    margin: 0 0 20px;
  }
`;

const DealerLocationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locations, setLocations] = useState<DealerLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const dealerUser = user?.userType === 'dealer' ? user : null;

  useEffect(() => {
    if (dealerUser?.dealerId) {
      loadLocations();
    }
  }, [dealerUser?.dealerId]);

  const loadLocations = async () => {
    if (!dealerUser?.dealerId) return;
    
    try {
      setIsLoading(true);
      const response = await apiService.getDealerLocations(dealerUser.dealerId);
      
      if (response.success && response.data) {
        let availableLocations = response.data;
        
        // Filter locations for staff users based on their assignments
        if (dealerUser.role === 'dealer_staff') {
          const userLocationIds = (dealerUser as any).locationIds || [];
          availableLocations = response.data.filter(loc => 
            userLocationIds.includes(loc.id)
          );
        }
        
        setLocations(availableLocations);
      } else {
        toast.error('Failed to load locations');
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dealer-dashboard');
  };

  if (!dealerUser) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Access denied. This page is only available to dealer users.
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack}>
          <ArrowLeft />
          Back to Dashboard
        </BackButton>
        <h1>
          <MapPin />
          Manage Locations
        </h1>
      </PageHeader>

      {isLoading ? (
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      ) : locations.length === 0 ? (
        <EmptyState>
          <h3>No locations found</h3>
          <p>Contact your administrator to add locations for your dealership.</p>
        </EmptyState>
      ) : (
        <LocationsGrid>
          {locations.map((location) => (
            <LocationCard key={location.id}>
              <div className="location-header">
                <div className="location-name">
                  <MapPin size={20} />
                  {location.name}
                </div>
              </div>
              
              <div className="location-details">
                {location.address && (
                  <div className="detail-item">
                    <div className="label">Address</div>
                    <div>{location.address}</div>
                  </div>
                )}
                {(location.city || location.state || location.zipCode) && (
                  <div className="detail-item">
                    <div className="label">City, State ZIP</div>
                    <div>{location.city}, {location.state} {location.zipCode}</div>
                  </div>
                )}
                {location.phone && (
                  <div className="detail-item">
                    <div className="label">Phone</div>
                    <div>{location.phone}</div>
                  </div>
                )}
                {location.email && (
                  <div className="detail-item">
                    <div className="label">Email</div>
                    <div>{location.email}</div>
                  </div>
                )}
              </div>
              
              <div className="location-actions">
                <Button 
                  variant="primary" 
                  onClick={() => navigate(`/locations/${location.id}/hours`)}
                >
                  <Clock />
                  Manage Hours
                </Button>
                <Button 
                  onClick={() => navigate(`/locations/${location.id}/bays`)}
                >
                  <Settings />
                  Manage Bays
                </Button>
                <Button 
                  onClick={() => navigate(`/locations/${location.id}/services`)}
                >
                  <Wrench />
                  Assign Services
                </Button>
              </div>
            </LocationCard>
          ))}
        </LocationsGrid>
      )}
    </PageContainer>
  );
};

export default DealerLocationsPage;
